
import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Category } from "@/lib/data";
import { categoryIcons } from "./navbar/CategoryIcons";

interface CategoryProps {
  category: Category;
}

const CategoryCard: React.FC<CategoryProps> = ({ category }) => {
  const { id, name, description, count = 0 } = category;
  
  // Get the icon component from our categoryIcons map
  const IconComponent = categoryIcons[name];
  
  // Default color based on category name (for consistency)
  const getColorClass = (categoryName: string): string => {
    const colorMap: Record<string, string> = {
      "Productivity": "bg-purple-500",
      "Creative": "bg-pink-500",
      "Business": "bg-blue-500",
      "Education": "bg-green-500",
      "Development": "bg-indigo-500",
      "Writing": "bg-yellow-500",
      "Research": "bg-cyan-500",
      "Data Analysis": "bg-sky-500",
      "Marketing": "bg-red-500",
      "Design": "bg-orange-500",
      "Entertainment": "bg-violet-500",
      "Health": "bg-emerald-500"
    };
    
    return colorMap[categoryName] || "bg-gray-500";
  };

  const getGradientClass = (categoryName: string): string => {
    const gradientMap: Record<string, string> = {
      "Productivity": "from-purple-500 to-indigo-600",
      "Creative": "from-pink-500 to-rose-600",
      "Business": "from-blue-500 to-cyan-600",
      "Education": "from-green-500 to-emerald-600",
      "Development": "from-indigo-500 to-blue-600",
      "Writing": "from-yellow-500 to-amber-600",
      "Research": "from-cyan-500 to-blue-600",
      "Data Analysis": "from-sky-500 to-blue-600",
      "Marketing": "from-red-500 to-pink-600",
      "Design": "from-orange-500 to-amber-600",
      "Entertainment": "from-violet-500 to-purple-600",
      "Health": "from-emerald-500 to-green-600"
    };
    
    return gradientMap[categoryName] || "from-gray-500 to-slate-600";
  };
  
  return (
    <Link to={`/category/${encodeURIComponent(name.toLowerCase())}`}>
      <Card className="overflow-hidden h-full hover:shadow-md transition-all border-transparent hover:border-gray-200 group">
        <div className={`h-3 overflow-hidden bg-gradient-to-br ${getGradientClass(name)}`} />
        <CardContent className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className={`${getColorClass(name)} text-white w-8 h-8 rounded-full flex items-center justify-center shadow-sm`}>
              {IconComponent && <IconComponent className="h-4 w-4" />}
            </div>
            <h3 className="font-medium text-lg">{name}</h3>
          </div>
          <p className="text-muted-foreground text-sm">{description}</p>
        </CardContent>
        <CardFooter className="px-6 py-4 border-t bg-muted/40">
          <p className="text-sm text-muted-foreground">
            {count} {count === 1 ? 'agent' : 'agents'}
          </p>
        </CardFooter>
      </Card>
    </Link>
  );
}

export default CategoryCard;
